// src/components/VariantSelect.tsx

import {
  Autocomplete,
  TextField,
  CircularProgress,
  Chip,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { Variant as ApiVariant } from '../../../../services/api';

export interface Variant {
  variantId: string;
  selectedOptions: Array<{
    optionValue: { internalName: string };
  }>;
  [key: string]: any;
}

export interface Product {
  productId: string;
  variants?: Variant[];
  [key: string]: any;
}

interface VariantSelectProps {
  /** Controlled selected value(s) */
  value: Variant | null | Variant[];
  onChange: (variant: Variant | null | Variant[]) => void;
  /** The product whose variants we want to list */
  product: Product | null;
  /** Allow selecting more than one */
  multiple?: boolean;
}

export function VariantSelect({
  value,
  onChange,
  product,
  multiple = false,
}: VariantSelectProps) {
  // Get variants directly from the product object
  const [variants, setVariants] = useState<ApiVariant[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!product?.productId) {
      setVariants([]);
      return;
    }
    
    setLoading(true);
    // Use variants from the product object instead of making an API call
    if (product.variants && Array.isArray(product.variants)) {
      setVariants(product.variants);
    } else {
      setVariants([]);
    }
    setLoading(false);
  }, [product?.productId, product?.variants]);

  // Build a display label from all selectedOptions' internalName
  const getLabel = (v: Variant) => {
    if (!v || !Array.isArray(v.selectedOptions) || v.selectedOptions.length === 0) return '';
    return v.selectedOptions.map((so) => so?.optionValue?.internalName || '').join(', ');
  };

  // Make sure our controlled `value` never defaults to the *entire* list
  // If multiple: value must be an array; otherwise null
  const safeValue = multiple
    ? Array.isArray(value)
      ? value
      : []
    : value ?? null;

  return (
    <Autocomplete
      multiple={multiple}
      options={Array.isArray(variants) ? variants : []}
      disabled={!product?.productId}
      disableCloseOnSelect={multiple}
      filterSelectedOptions
      noOptionsText="No variants available"

      // Control selection state properly
      value={safeValue}
      onChange={(_, newVal) => onChange(newVal)}

      // Use our custom label for both list and chips
      getOptionLabel={getLabel}

      // Tell MUI how to tell two variants apart
      isOptionEqualToValue={(opt, val) =>
        opt.variantId === (val as Variant)?.variantId
      }

      // Render each dropdown item with a unique key
      renderOption={(props, option) => {
        if (!option) return null;
        const label = getLabel(option);
        return (
          <li {...props} key={option.variantId}>
            {label || 'Select Variant'}
          </li>
        );
      }}

      // Render selected chips with a unique key each
      renderTags={(tagValue, getTagProps) => {
        if (!multiple || !Array.isArray(tagValue)) return [];
        return tagValue.map((option, idx) => {
          if (!option) return null;
          const label = getLabel(option);
          return (
            <Chip
              {...getTagProps({ index: idx })}
              key={option.variantId}
              size="small"
              label={label}
            />
          );
        });
      }}

      // The text‐input part
      renderInput={(params) => (
        <TextField
          {...params}
          label="Select Variant"
          placeholder={
            product?.productId
              ? 'Search variants…'
              : 'Select a product first'
          }
          fullWidth
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading && <CircularProgress data-testid="loading-indicator" size={20} />}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
}
